import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Ticket, DisputeReasonData, UploadProgress, TicketFields } from '../types';
import { AppealSession } from '../domain/models';

interface TicketContextType {
  appealSession: AppealSession | null;
  currentTicket: Ticket | null;
  ticketFields: TicketFields | null;
  disputeData: DisputeReasonData | null;
  uploadProgress: UploadProgress;
  setAppealSession: (session: AppealSession | null) => void;
  setCurrentTicket: (ticket: Ticket | null) => void;
  setTicketFields: (fields: TicketFields | null) => void;
  setDisputeData: (data: DisputeReasonData | null) => void;
  setUploadProgress: (progress: UploadProgress | ((prev: UploadProgress) => UploadProgress)) => void;
  updateDisputeData: (updates: Partial<DisputeReasonData>) => void;
  setSessionFromTicketResponse: (payload: {
    ticket?: Ticket | null;
    fields?: TicketFields | null;
    ticketId?: string | null;
  }) => void;
  clearTicketData: () => void;
}

const TicketContext = createContext<TicketContextType | undefined>(undefined);

export const useTicket = (): TicketContextType => {
  const context = useContext(TicketContext);
  if (context === undefined) {
    throw new Error('useTicket must be used within a TicketProvider');
  }
  return context;
};

interface TicketProviderProps {
  children: ReactNode;
}

const initialUploadProgress: UploadProgress = {
  progress: 0,
  status: 'idle',
};

const initialDisputeData: DisputeReasonData = {
  emergencySituation: '',
  signVisibility: undefined,
  weatherConditions: '',
  evidenceFiles: [],
  commonReasons: [],
  additionalComments: '',
};

export const TicketProvider: React.FC<TicketProviderProps> = ({ children }) => {
  const [appealSession, setAppealSession] = useState<AppealSession | null>(null);
  const [currentTicket, setCurrentTicket] = useState<Ticket | null>(null);
  const [ticketFields, setTicketFields] = useState<TicketFields | null>(null);
  const [disputeData, setDisputeData] = useState<DisputeReasonData | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(initialUploadProgress);

  const updateDisputeData = (updates: Partial<DisputeReasonData>) => {
    setDisputeData(prev => prev ? { ...prev, ...updates } : { ...initialDisputeData, ...updates });
  };

  const setSessionFromTicketResponse = (payload: {
    ticket?: Ticket | null;
    fields?: TicketFields | null;
    ticketId?: string | null;
  }) => {
    const { ticket, fields, ticketId } = payload;
    const sessionId = ticketId ?? ticket?.id ?? null;
    setCurrentTicket(ticket ?? null);
    setTicketFields(fields ?? null);
    if (sessionId) {
      setAppealSession({
        id: sessionId,
        status: 'CREATED',
      });
    }
  };

  const clearTicketData = () => {
    setAppealSession(null);
    setCurrentTicket(null);
    setTicketFields(null);
    setDisputeData(null);
    setUploadProgress(initialUploadProgress);
  };

  const value: TicketContextType = {
    appealSession,
    currentTicket,
    ticketFields,
    disputeData,
    uploadProgress,
    setAppealSession,
    setCurrentTicket,
    setTicketFields,
    setDisputeData,
    setUploadProgress,
    updateDisputeData,
    setSessionFromTicketResponse,
    clearTicketData,
  };

  return (
    <TicketContext.Provider value={value}>
      {children}
    </TicketContext.Provider>
  );
};
